import {
  actionColumn,
  BoundDateField,
  BoundNumberField,
  BoundSelectField,
  BoundTextField,
  Button,
  column,
  Css,
  emptyCell,
  GridDataRow,
  GridTable,
  GridTableApi,
  IconButton,
  simpleHeader,
  TextField,
  useComputed,
  useGridTableApi,
} from "@homebound/beam";
import { ObjectConfig, ObjectState, required, useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useCallback } from "react";
import { useHistory, useParams } from "react-router";
import { Price } from "src/components";
import { PdfViewer } from "src/components/PdfViewer";
import { getStage } from "src/context";
import {
  AssetInfoFragment,
  InputMaybe,
  Maybe,
  PropertySaleSettlementStatement_LotPartitionFragment,
  PropertySaleSettlementStatementFragment,
  PropertySaleSettlementStatementLineItemFragment,
  PropertySaleSettlementStatementLineItemType,
  SavePropertySaleSettlementStatementInput,
  useDeletePropertySaleSettlementStatementMutation,
  useProcessPropertySaleSettlementStatementOcrMutation,
  usePropertySaleSettlementStatementDocumentsQuery,
  useSavePropertySaleSettlementStatementEditorMutation,
  useSendPropertySaleSettlementStatementForBookingMutation,
} from "src/generated/graphql-types";
import { PageHeader } from "src/routes/layout/PageHeader";
import { DocumentIdParams } from "src/routes/routesDef";
import { createLotDetailsUrl, createLotPartitionDocumentsUrl } from "src/RouteUrls";
import { DateOnly } from "src/utils/dates";
import { queryResult } from "src/utils/queryResult";
import { fail, safeEntries } from "src/utils/utils";

export function PropertySaleSettlementStatementEditor() {
  const { documentId } = useParams<DocumentIdParams>();

  const documentsQuery = usePropertySaleSettlementStatementDocumentsQuery({
    variables: { documentIds: [documentId] },
    skip: !documentId,
  });

  return queryResult(documentsQuery, (data) => {
    const document = data.documents[0];
    const lotPartition = document.parent as PropertySaleSettlementStatement_LotPartitionFragment;
    const documentId = document.id;
    const psss = document.propertySaleSettlementStatement;
    // If it's new, it doesn't have a PSSS to this document yet
    if (!psss) {
      return (
        <PropertySaleSettlementStatementForm
          documentId={documentId}
          lotPartition={lotPartition}
          assetInfo={document.asset}
        />
      );
    }
    // Otherwise it does
    return (
      <PropertySaleSettlementStatementForm
        documentId={documentId}
        lotPartition={lotPartition}
        assetInfo={document.asset}
        propertySaleSettlementStatement={psss}
      />
    );
  });
}

type CreatePropertySaleSettlementStatementEditorProps = {
  documentId?: string;
  lotPartition: PropertySaleSettlementStatement_LotPartitionFragment;
  assetInfo?: AssetInfoFragment;
  propertySaleSettlementStatement?: PropertySaleSettlementStatementFragment;
};

function PropertySaleSettlementStatementForm({
  documentId,
  lotPartition,
  assetInfo,
  propertySaleSettlementStatement,
}: CreatePropertySaleSettlementStatementEditorProps) {
  const settlementStatement = propertySaleSettlementStatement;
  const [savePropertySaleSettlementStatement] = useSavePropertySaleSettlementStatementEditorMutation();
  const [deletePropertySaleSettlementStatement] = useDeletePropertySaleSettlementStatementMutation();
  const [processPropertySaleSettlementStatementOcr] = useProcessPropertySaleSettlementStatementOcrMutation();
  const [sendPropertySaleSettlementStatementForBooking] = useSendPropertySaleSettlementStatementForBookingMutation();
  const history = useHistory();

  const isBooked = settlementStatement?.bookingEventSent;

  const tableApi = useGridTableApi<Row>();
  const formState = useFormState({
    config: formConfig,
    init: {
      input: settlementStatement
        ? mapSettlementStatementToForm(settlementStatement)
        : { documentId, lotPartitionId: lotPartition.id, lineItems: [] },
    },
    readOnly: isBooked,
  });

  const saveLotAcqSettlementStatementFunction = useCallback(
    async (formState: ObjectState<PropertySaleSettlementStatementFormInput>) => {
      const result = await savePropertySaleSettlementStatement({
        variables: { input: mapToInput(formState.value) },
      });
      // Update form state with the saved data from the backend
      const savedStatement = result.data?.savePropertySaleSettlementStatement.propertySaleSettlementStatement;
      if (savedStatement) {
        formState.set(mapSettlementStatementToForm(savedStatement));
      }
      formState.commitChanges();
    },
    [savePropertySaleSettlementStatement],
  );

  function mapToInput(
    formStateValue: PropertySaleSettlementStatementFormInput,
  ): SavePropertySaleSettlementStatementInput {
    const { documentId, lotPartitionId, ...others } = formStateValue;
    return {
      ...others,
      documentId,
      lotPartitionId,
      settlementDate: formStateValue.settlementDate ? new DateOnly(formStateValue.settlementDate) : undefined,
      lineItems: formStateValue.lineItems,
    };
  }

  const title = settlementStatement
    ? "Edit Property Sale Settlement Statement for " +
      lotPartition.lot.address?.street1 +
      " (lot partition " +
      lotPartition.lot.id +
      ")"
    : "Create Property Sale Settlement Statement for " +
      lotPartition.lot.address?.street1 +
      " (lot partition " +
      lotPartition.lot.id +
      ")";

  const bookingTooltip = settlementStatement?.canSendForBooking.allowed
    ? "Books the Settlement Statement to Accounting - cannot be undone"
    : settlementStatement?.canSendForBooking.disabledReasons.map((reason) => reason.message).join(", ");

  return (
    <>
      <PageHeader title={title} backButton={createLotDetailsUrl(lotPartition.lot.id)} />
      <TextField
        label="Blueprint Project Id"
        value={lotPartition.blueprintProjectId || ""}
        readOnly
        onChange={() => {}}
      />
      <TextField
        label="Investment Status"
        value={settlementStatement?.lotPartition.lot.lotInvestmentStatus.name}
        readOnly
        onChange={() => {}}
      />
      <TextField
        label="Legal Entity Acquiring the Lot"
        value={settlementStatement?.lotPartition.lot.legalEntityName || ""}
        readOnly
        onChange={() => {}}
      />
      <BoundTextField field={formState.sellerName} label="Seller Name (from this document)" readOnly={isBooked} />
      <BoundDateField field={formState.settlementDate} label="Settlement Date" readOnly={isBooked} />
      Total Debits:
      <Price valueInCents={settlementStatement?.totalDebitsInCents} dropZero />
      Total Credits:
      <Price valueInCents={settlementStatement?.totalCreditsInCents} dropZero />
      <LineItemsTable formState={formState} tableApi={tableApi} />
      <div css={Css.df.fdr.gap1.$}>
        <Button
          variant="secondary"
          label="Add Line Item"
          onClick={() => {
            const newLineItems = [
              ...formState.lineItems.value,
              {
                id: undefined,
                amountInCents: 0,
                type: PropertySaleSettlementStatementLineItemType.Other,
                notes: "",
              },
            ];
            formState.lineItems.set(newLineItems);
          }}
          disabled={isBooked}
        />
        <Button
          variant="secondary"
          label="Reprocess OCR"
          onClick={async () => {
            await processPropertySaleSettlementStatementOcr({ variables: { input: { id: settlementStatement!.id } } });
            window.location.reload();
          }}
          disabled={isBooked}
        />
        <Button
          variant="primary"
          label="Save"
          onClick={async () => {
            await saveLotAcqSettlementStatementFunction(formState);
          }}
          disabled={isBooked}
        />
        <Button
          variant="secondary"
          label="Book To Accounting"
          icon="finances"
          onClick={async () => {
            // Send to booking
            await sendPropertySaleSettlementStatementForBooking({
              variables: { input: { id: settlementStatement!.id } },
            });
            // Then send them back to the lot partition
            history.push({
              pathname: createLotPartitionDocumentsUrl(lotPartition.lot.id, lotPartition.id),
            });
          }}
          disabled={!settlementStatement?.canSendForBooking.allowed}
          tooltip={bookingTooltip}
        />
        <IconButton
          icon="trash"
          tooltip="Delete Lot Acq Settlement Statement"
          onClick={async () => {
            await deletePropertySaleSettlementStatement({
              variables: {
                input: { id: settlementStatement?.id ?? fail("No settlement statement id") },
              },
            });
            history.push({
              pathname: createLotPartitionDocumentsUrl(lotPartition.lot.id, lotPartition.id),
            });
          }}
          disabled={isBooked}
        />
      </div>
      <PdfViewer
        hasHeader
        title={"Uploaded Settlement Statement PDF"}
        assets={[assetInfo]}
        pdfPageWidth={getDynamicPdfVierwerWidth()}
      />
    </>
  );
}

// When lineItems change (e.g., adding/removing rows), Observer detects the change
// and triggers a re-render. Without it, the GridTable won't update when items are added.
function LineItemsTable({
  formState,
  tableApi,
}: {
  formState: ObjectState<PropertySaleSettlementStatementFormInput>;
  tableApi: GridTableApi<Row>;
}) {
  const rows = useComputed(() => formState.lineItems.rows, [formState.lineItems.rows]);
  return (
    <Observer>
      {() => {
        return <GridTable columns={createColumns(formState, tableApi)} rows={createRows(rows)} api={tableApi} />;
      }}
    </Observer>
  );
}

function createColumns(formState: ObjectState<PropertySaleSettlementStatementFormInput>, tableApi: GridTableApi<Row>) {
  return [
    column<Row>({
      header: () => "Type",
      lineItem: (row) => (
        <BoundSelectField
          field={row.type}
          options={safeEntries(PropertySaleSettlementStatementLineItemType)}
          getOptionValue={([, code]) => code}
          getOptionLabel={([name]) => name}
        />
      ),
    }),
    column<Row>({
      header: () => "Amount",
      lineItem: (row) => ({
        value: () => row.amountInCents.value,
        content: () => <BoundNumberField type="cents" field={row.amountInCents} />,
        tooltip: "The amount of the line item",
      }),
      w: "200px",
    }),
    column<Row>({
      header: () => "Notes",
      lineItem: (row) => <BoundTextField field={row.notes} />,
      w: "600px",
    }),
    actionColumn<Row>({
      header: () => "Delete",
      lineItem: (_, { row }) => (
        <IconButton
          icon="trash"
          onClick={() => {
            const rows = tableApi.getVisibleRows("lineItem");
            const index = rows.findIndex((r) => r.id === row.id);
            formState.lineItems.set(formState.lineItems.rows.filter((_, idx) => idx !== index).map((r) => r.value));
            formState.commitChanges();
          }}
        />
      ),
    }),
  ];
}

function createRows(
  lineItems: ObjectState<PropertySaleSettlementStatementFormInput>["lineItems"]["rows"],
): GridDataRow<Row>[] {
  const lineItemRows = lineItems.map((lineItemField, index) => ({
    kind: "lineItem" as const,
    id: lineItemField.id.value || `new-${index}`,
    data: lineItemField,
  })) as LineItemRow[];
  return [simpleHeader, ...lineItemRows];
}

export type PropertySaleSettlementStatementFormInput = {
  id: InputMaybe<string>;
  lotPartitionId: InputMaybe<string>;
  documentId: InputMaybe<string>;
  sellerName: InputMaybe<string>;
  settlementDate: InputMaybe<Date>;
  lineItems: {
    id: InputMaybe<string>;
    type: InputMaybe<PropertySaleSettlementStatementLineItemType>;
    amountInCents: InputMaybe<number>;
    notes: InputMaybe<string>;
  }[];
};

export const formConfig: ObjectConfig<PropertySaleSettlementStatementFormInput> = {
  id: { type: "value" },
  lotPartitionId: { type: "value", rules: [required] },
  documentId: { type: "value", rules: [required] },
  sellerName: { type: "value", rules: [required] },
  settlementDate: { type: "value", rules: [required] },
  lineItems: {
    type: "list",
    config: {
      id: { type: "value" },
      type: { type: "value" },
      amountInCents: { type: "value" },
      notes: { type: "value" },
    },
  },
};

function mapSettlementStatementToForm(
  settlementStatement: PropertySaleSettlementStatementFragment,
): PropertySaleSettlementStatementFormInput {
  return {
    id: settlementStatement.id,
    lotPartitionId: settlementStatement.lotPartition.id,
    documentId: settlementStatement.document.id,
    sellerName: settlementStatement.sellerName,
    settlementDate: settlementStatement.settlementDate
      ? new Date(settlementStatement.settlementDate.toISOString())
      : undefined,
    lineItems: mapSettlementStatementLineItemsToForm(settlementStatement.lineItems),
  };
}

export function mapSettlementStatementLineItemsToForm(
  lineItems: Maybe<PropertySaleSettlementStatementLineItemFragment[]>,
) {
  const lines = lineItems?.map((li) => ({
    id: li.id,
    amountInCents: li.amountInCents,
    type: li.type.code,
    notes: li.notes,
  }));
  return lines || [];
}

export function getDynamicPdfVierwerWidth(browserWidth?: number) {
  const { innerHeight } = window;
  return innerHeight > 900 ? 820 : innerHeight > 800 ? 760 : 640;
}

type HeaderRow = { kind: "header" };
type LineItemRow = {
  kind: "lineItem";
  id: string;
  data: ObjectState<{
    id: InputMaybe<string>;
    type: InputMaybe<PropertySaleSettlementStatementLineItemType>;
    amountInCents: InputMaybe<number>;
    notes: InputMaybe<string>;
  }>;
};
type Row = HeaderRow | LineItemRow;
